/**
 * Helper functions for Vue Bridge container animations and state management
 */

/**
 * Shared state for Vue Bridge
 */
export class VueBridgeState {
  static isUserInsideVueBridge = false;
  static onCompleteCallback: (() => void) | null = null;
  static onProfileBackCallback: (() => void) | null = null;
  static onVisibilityChange: ((value: boolean) => void) | null = null;
  static retireStaleLandingLayer: (() => void) | null = null;

  static setUserInsideVueBridge(value: boolean) {
    if (VueBridgeState.isUserInsideVueBridge === value) {
      return;
    }
    VueBridgeState.isUserInsideVueBridge = value;
    if (VueBridgeState.onVisibilityChange) {
      VueBridgeState.onVisibilityChange(value);
    }
  }
}

export enum TransitionType {
  SLIDE_ANIMATION = 'slide-animation',
  DESTRUCT = 'destruct'
}

export class VueBridgeContainerHelpers {
  /**
   * Sets the container to hidden state
   * @param container - The HTML element container for the Vue iframe
   */
  static setContainerHidden(container: HTMLElement | null) {
    if (container) {
      container.style.display = 'none';
      container.style.opacity = '0';
      container.style.transform = 'translateX(0)';
    }
  }
  
  /**
   * @param container - The HTML element container for the Vue iframe
   */
  static prepareSlideIn(container: HTMLElement | null) {
    if (container) {
      container.style.opacity = '0';
      container.style.transform = 'translateX(100%)'; // Start from right
      container.style.display = 'block';
    }
  }

  /**
   * Triggers slide-in fade-in animation (right to left)
   * @param container - The HTML element container for the Vue iframe
   */
  static triggerSlideIn(container: HTMLElement | null) {
    if (container) {
      container.style.opacity = '1';
      container.style.transform = 'translateX(0)'; // Slide to center
    }
  }

  /**
   * Triggers slide-out fade-out animation (left to right)
   * @param container - The HTML element container for the Vue iframe
   */
  static triggerSlideOut(container: HTMLElement | null) {
    if (container) {
      container.style.opacity = '0';
      container.style.transform = 'translateX(100%)'; // Slide to right
    }
  }


  /**
   * Shows container with destruct animation
   * @param container - The HTML element container for the Vue iframe
   */
  static prepareDestructIn(container: HTMLElement | null) {
    if (container) {
      container.style.opacity = '0';
      container.style.transform = 'translateX(0)'; // No movement
      container.style.display = 'block';
    }
  }

  
  /**
   * Triggers destruct fade-out animation
   * @param container - The HTML element container for the Vue iframe
   */
  static triggerDestruct(container: HTMLElement | null) {
    if (container) {
      // Only fade out, no transform - ensure no movement
      container.style.opacity = '0';
      // Explicitly set transform to none to override any previous transforms
      container.style.transform = 'translateX(0) translateY(0)';
    }
  }


  /**
   * Forces browser reflow/repaint
   * Useful to ensure styles are applied before animations
   * @param container - The HTML element container for the Vue iframe
   */
  static forceReflow(container: HTMLElement | null) {
    if (container) {
      void container.offsetHeight;
    }
  }

  /**
   * Resolves when the container's transform transition ends (the slide-in has covered the
   * app), with a timer fallback in case the browser never fires the event.
   */
  static waitForSlideIn(container: HTMLElement | null, fallbackMs: number = 450): Promise<void> {
    return new Promise<void>((resolve) => {
      if (!container) {
        resolve();
        return;
      }
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        container.removeEventListener('transitionend', onEnd);
        resolve();
      };
      const onEnd = (event: TransitionEvent) => {
        if (event.target === container && event.propertyName === 'transform') finish();
      };
      container.addEventListener('transitionend', onEnd);
      setTimeout(finish, fallbackMs);
    });
  }

  static isContainerVisible(container: HTMLElement | null): boolean {
    if (container) {
      return container.style.display !== 'none' && container.style.opacity !== '0';
    }
    return false;
  }
}


// WEBPACK FOOTER //
// ./src/providers/vue/vue-helpers.ts